import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import {
  getUserRoleInCompany,
  getCurrentUser,
} from "@/lib/stores/unifiedUserStore";
import apiService from "@/lib/api-functions/api-service";
import { Button } from     "@/components/ui/button";
import { Copy, Plus, Trash2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { format } from "date-fns";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

interface CompanyRole {
  id: string;
  role_name: string;
  description?: string;
  is_built_in: boolean;
  data?: {
    color?: string;
  };
}

interface InvitationCode {
  id: string;
  company_id: string;
  code: string;
  code_name: string;
  created_by: string;
  created_by_name: string;
  game_ids: string[];
  role: string;
  expires_at?: string;
  allowed_email_domains?: string[];
  is_single_use?: boolean;
  target_email?: string;
  created_at: string;
  used_at?: string;
  used_by?: string;
  used_by_name?: string;
  usage_count?: number;
}

type InvitationCodesSectionProps = Record<string, never>;

export default function InvitationCodesSection(
  _props: InvitationCodesSectionProps
) {
  const { t } = usePlatformTranslation();
  const { toast } = useToast();
  const { selectedCompany, games } = useSimplifiedUserData();
  const currentUser = getCurrentUser();

  const [codes, setCodes] = useState<InvitationCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [selectedGameIds, setSelectedGameIds] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [codeName, setCodeName] = useState<string>("");
  const [allowedDomains, setAllowedDomains] = useState<string>("");
  const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);
  const [codeUsers, setCodeUsers] = useState<Record<string, any[]>>({});
  const [companyRoles, setCompanyRoles] = useState<CompanyRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  // Check if current user has permission to manage codes (owner only)
  const userRole = currentUser && selectedCompany 
    ? getUserRoleInCompany(selectedCompany.id, currentUser.id)
    : null;
  const hasPermission = userRole?.toLowerCase() === 'owner';

  const loadCodes = useCallback(async () => {
    if (!selectedCompany) return;

    setLoading(true);
    try {
      const response = await apiService.getInvitationCodes(
        selectedCompany.id,
        true
      );
      if (response.success && response.data) {
        setCodes(response.data);
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to load invitation codes",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error loading invitation codes:", error);
      toast({
        title: "Error",
        description: "Failed to load invitation codes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, toast]);

  const loadCompanyRoles = useCallback(async () => {
    if (!selectedCompany) return;

    setRolesLoading(true);
    try {
      const response = await apiService.getCompanyRoles(selectedCompany.id);
      if (response.success && response.data) {
        setCompanyRoles(response.data);
        // Set default selected role to the first non-owner role, or first role if none available
        if (response.data.length > 0 && !selectedRole) {
          const defaultRole = response.data.find((r: CompanyRole) => r.role_name.toLowerCase() !== 'owner') || response.data[0];
          setSelectedRole(defaultRole.role_name.toLowerCase());
        }
      } else {
        console.error("Failed to load company roles:", response.error);
      }
    } catch (error) {
      console.error("Error loading company roles:", error);
    } finally {
      setRolesLoading(false);
    }
  }, [selectedCompany, selectedRole]);

  // Load invitation codes and roles
  useEffect(() => {
    if (selectedCompany && hasPermission) {
      loadCodes();
      loadCompanyRoles();
    }
  }, [selectedCompany, hasPermission, loadCodes, loadCompanyRoles]);

  const handleGenerateCode = async () => {
    if (!selectedCompany) return;

    if (!codeName.trim()) {
      toast({
        title: "Code Name Required",
        description: "Please provide a name for this invitation code.",
        variant: "destructive",
      });
      return;
    }

    if (selectedGameIds.length === 0) {
      toast({
        title: "No Games Selected",
        description: "Please select at least one game for the invitation code.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedRole) {
      toast({
        title: "No Role Selected",
        description: "Please select a role for the invitation code.",
        variant: "destructive",
      });
      return;
    }

    // Parse and validate email domains if provided
    let emailDomains: string[] | undefined = undefined;
    if (allowedDomains.trim()) {
      emailDomains = allowedDomains
        .split(',')
        .map(d => d.trim())
        .filter(d => d.length > 0)
        .map(d => d.startsWith('@') ? d : `@${d}`);

      // Validate domain format
      for (const domain of emailDomains) {
        if (!domain.match(/^@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
          toast({
            title: "Invalid Domain Format",
            description: `"${domain}" is not a valid email domain. Use format: @example.com`,
            variant: "destructive",
          });
          return;
        }
      }
    }

    setGenerating(true);
    try {
      const response = await apiService.generateInvitationCode({
        company_id: selectedCompany.id,
        code_name: codeName.trim(),
        game_ids: selectedGameIds,
        role: selectedRole,
        expires_at: expirationDate || undefined,
        allowed_email_domains: emailDomains,
      });

      if (response.success && response.data) {
        toast({
          title: "Code Generated",
          description: `Invitation code "${response.data.code_name}" (${response.data.code}) has been created successfully.`,
        });
        setShowGenerateForm(false);
        setCodeName("");
        setAllowedDomains("");
        setSelectedGameIds([]);
        // Reset to default role (first non-owner role or first available)
        const defaultRole = companyRoles.find((r) => r.role_name.toLowerCase() !== 'owner') || companyRoles[0];
        setSelectedRole(defaultRole?.role_name.toLowerCase() || "");
        setExpirationDate("");
        loadCodes();
      } else {
        toast({
          title: "Generation Failed",
          description: response.error || "Failed to generate invitation code",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error generating code:", error);
      toast({
        title: "Error",
        description: "Failed to generate invitation code",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeCode = async (codeId: string) => {
    try {
      const response = await apiService.revokeInvitationCode(codeId);
      if (response.success) {
        toast({
          title: "Code Revoked",
          description: "The invitation code has been revoked successfully.",
        });
        loadCodes();
      } else {
        toast({
          title: "Revocation Failed",
          description: response.error || "Failed to revoke invitation code",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error revoking code:", error);
      toast({
        title: "Error",
        description: "Failed to revoke invitation code",
        variant: "destructive",
      });
    }
  };

  const copyCodeToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Copied",
      description: "Invitation code copied to clipboard",
    });
  };

  const toggleCodeUsers = async (codeId: string) => {
    if (expandedCodeId === codeId) {
      setExpandedCodeId(null);
      return;
    }

    setExpandedCodeId(codeId);

    // Load users if not already loaded
    if (!codeUsers[codeId]) {
      try {
        const response = await apiService.getInvitationCodeUsers(codeId);
        if (response.success && response.data) {
          setCodeUsers(prev => ({
            ...prev,
            [codeId]: response.data
          }));
        } else {
          toast({
            title: "Error",
            description: response.error || "Failed to load users",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error loading code users:", error);
        toast({
          title: "Error",
          description: "Failed to load users for this code",
          variant: "destructive",
        });
      }
    }
  };

  const getCodeStatus = (code: InvitationCode) => {
    // Single-use codes that are used
    if (code.is_single_use && code.used_at) {
      return { status: "used", label: "Used", icon: CheckCircle2, color: "text-gray-500" };
    }
    // Expired codes
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return { status: "expired", label: "Expired", icon: XCircle, color: "text-red-500" };
    }
    // Active codes
    return { status: "active", label: "Active", icon: Clock, color: "text-green-500" };
  };

  const getGameNames = (gameIds: string[] | string | null | undefined) => {
    // Ensure gameIds is an array
    const ids = Array.isArray(gameIds) 
      ? gameIds 
      : (typeof gameIds === 'string' 
        ? JSON.parse(gameIds) 
        : []);
    
    return ids
      .map((id: string) => games.find((g) => g.id === id)?.name || "Unknown Game")
      .join(", ");
  };

  if (!hasPermission) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded">
        <p className="text-sm text-yellow-800">
          You need owner permissions to manage invitation codes.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('platform.settings.invitationcodessection.invitation_codes')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            Generate and manage invitation codes for your team members
          </p>
        </div>
        <Button
          onClick={() => setShowGenerateForm(!showGenerateForm)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Generate Code
        </Button>
      </div>

      {/* Generate Code Form */}
      {showGenerateForm && (
        <div className="bg-purple-50 border border-purple-200 rounded p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Generate New Invitation Code
          </h3>

          {/* Code Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Code Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={codeName}
              onChange={(e) => setCodeName(e.target.value)}
              placeholder={t('platform.settings.invitationcodessection.eg_paradox_team_external_translators')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              A friendly name to identify this invitation code and group users
            </p>
          </div>

          {/* Game Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Games <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              {games.length === 0 ? (
                <p className="text-sm text-gray-500">{t('platform.settings.invitationcodessection.no_games_available')}</p>
              ) : (
                games.map((game) => (
                  <label
                    key={game.id}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedGameIds.includes(game.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGameIds([...selectedGameIds, game.id]);
                        } else {
                          setSelectedGameIds(
                            selectedGameIds.filter((id) => id !== game.id)
                          );
                        }
                      }}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{game.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
              disabled={rolesLoading}
            >
              {rolesLoading ? (
                <option value="">Loading roles...</option>
              ) : companyRoles.length === 0 ? (
                <option value="">No roles available</option>
              ) : (
                companyRoles.map((role) => (
                  <option key={role.id} value={role.role_name.toLowerCase()}>
                    {role.role_name}
                  </option>
                ))
              )}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Roles are configured in Permissions & Roles settings
            </p>
          </div>

          {/* Allowed Email Domains */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Allowed Email Domains (Optional)
            </label>
            <textarea
              value={allowedDomains}
              onChange={(e) => setAllowedDomains(e.target.value)}
              placeholder={t('platform.settings.invitationcodessection.paradoxcom_paradoxse')}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows={2}
            />
            <p className="text-xs text-gray-500 mt-1">
              Comma-separated list of email domains (e.g., @paradox.com, @example.org). Leave empty to allow any email domain.
            </p>
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiration Date (Optional)
            </label>
            <input
              type="datetime-local"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty for no expiration
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleGenerateCode}
              disabled={generating || selectedGameIds.length === 0}
              className="flex-1"
            >
              {generating ? "Generating..." : "Generate Code"}
            </Button>
            <Button
              onClick={() => {
                setShowGenerateForm(false);
                setCodeName("");
                setAllowedDomains("");
                setSelectedGameIds([]);
                // Reset to default role (first non-owner role or first available)
                const defaultRole = companyRoles.find((r) => r.role_name.toLowerCase() !== 'owner') || companyRoles[0];
                setSelectedRole(defaultRole?.role_name.toLowerCase() || "");
                setExpirationDate("");
              }}
              variant="outline"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Codes List */}
      <div className="bg-transparent border border-muted rounded overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-gray-500">{t('platform.settings.invitationcodessection.loading_codes')}</div>
        ) : codes.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No invitation codes yet. Generate one to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Games
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Used By
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-muted">
                {codes.map((code) => {
                  const statusInfo = getCodeStatus(code);
                  const StatusIcon = statusInfo.icon;

                  return (
                    <tr key={code.id} className=" hover:bg-surface">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {code.code}
                          </span>
                          <button
                            onClick={() => copyCodeToClipboard(code.code)}
                            className="text-gray-400 hover:text-gray-600"
                            title={t('platform.settings.invitationcodessection.copy_code')}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`flex items-center gap-1 ${statusInfo.color}`}>
                          <StatusIcon className="w-4 h-4" />
                          <span className="text-sm font-medium">
                            {statusInfo.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {getGameNames(code.game_ids)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-900 capitalize">
                          {code.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {code.created_by_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {code.expires_at
                            ? format(new Date(code.expires_at), "MMM d, yyyy")
                            : "Never"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {code.used_by_name ? (
                          <div className="text-sm">
                            <div className="text-gray-900">{code.used_by_name}</div>
                            <div className="text-xs text-gray-500">
                              {code.used_at &&
                                format(new Date(code.used_at), "MMM d, yyyy")}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {/* Show delete for: unused codes OR used single-use codes */}
                        {(!code.used_at || (code.is_single_use && code.used_at)) && (
                          <button
                            onClick={() => handleRevokeCode(code.id)}
                            className="text-red-600 hover:text-red-800"
                            title={code.used_at ? t('platform.settings.invitationcodessection.remove_code') : t('platform.settings.invitationcodessection.revoke_code')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded p-4">
        <p className="text-sm text-blue-800">
          <strong>{t('platform.settings.invitationcodessection.how_it_works')}</strong> Invitation codes are single-use codes that grant immediate access to selected games with the specified role. Once used, they cannot be reused. Share these codes securely with your team members during their onboarding process.
        </p>
      </div>
    </div>
  );
}

